import { Button, Checkbox, Group, LoadingOverlay, Modal, Select, Stack, Text, TextInput, Box, Alert, Table, ScrollArea, Divider } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react'
import { useEligibleRecipients, useSendPreRegisterEmails, useSendTestEmail, SendPreRegisterEmailsResponse, SendEmailResult } from 'api/query/pre-register'
import { useTradeshows } from 'api/query/tradeshow'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

interface PreRegisterEmailModalProps {
  opened: boolean
  onClose: () => void
  targetTradeShowId: string
  targetTradeShowName: string
}

export function PreRegisterEmailModal({ opened, onClose, targetTradeShowId, targetTradeShowName }: PreRegisterEmailModalProps) {
  const { t } = useTranslation()
  const { data: tradeshows } = useTradeshows()
  const [sourceTradeShowId, setSourceTradeShowId] = useState<string>()
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set())
  const [testEmail, setTestEmail] = useState('')
  const [sendingTest, setSendingTest] = useState(false)
  const [sending, setSending] = useState(false)
  const [sendResults, setSendResults] = useState<SendPreRegisterEmailsResponse | undefined>()

  const { data: eligibleRecipients, isFetching: loadingRecipients } = useEligibleRecipients(sourceTradeShowId, targetTradeShowId)
  const { mutateAsync: sendTestEmail } = useSendTestEmail(targetTradeShowId)
  const { mutateAsync: sendPreRegisterEmails } = useSendPreRegisterEmails(targetTradeShowId)

  // Reset selected emails when eligible recipients change
  useEffect(() => {
    setSelectedEmails(new Set())
  }, [eligibleRecipients])

  const handleSourceChange = (value: string | null) => {
    if (value) {
      setSourceTradeShowId(value)
      setSendResults(undefined)
    }
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked && eligibleRecipients) {
      setSelectedEmails(new Set(eligibleRecipients.map((r) => r.email)))
    } else {
      setSelectedEmails(new Set())
    }
  }

  const handleSelectRecipient = (email: string, checked: boolean) => {
    const newSelected = new Set(selectedEmails)
    if (checked) {
      newSelected.add(email)
    } else {
      newSelected.delete(email)
    }
    setSelectedEmails(newSelected)
  }

  const handleSendTest = async () => {
    if (!testEmail) {
      showNotification({
        message: 'Please enter a test email address',
        color: 'red',
      })
      return
    }

    setSendingTest(true)
    try {
      await sendTestEmail({ testEmail, firstName: 'Test', lastName: 'User', companyName: 'Test Company' })
      showNotification({
        message: `Test email sent to ${testEmail}`,
        color: 'green',
      })
      setTestEmail('')
    } catch (error) {
      showNotification({
        message: 'Failed to send test email',
        color: 'red',
      })
    }
    setSendingTest(false)
  }

  const handleSendEmails = async () => {
    if (!sourceTradeShowId || selectedEmails.size === 0) {
      showNotification({
        message: 'Please select recipients',
        color: 'red',
      })
      return
    }

    setSending(true)
    try {
      const response = await sendPreRegisterEmails({
        sourceTradeShowId,
        recipientEmails: Array.from(selectedEmails),
      })

      setSendResults(response.data)

      showNotification({
        message: `${response.data.successCount} emails sent successfully, ${response.data.failureCount} failed`,
        color: response.data.failureCount > 0 ? 'yellow' : 'green',
      })
    } catch (error) {
      showNotification({
        message: 'Failed to send emails',
        color: 'red',
      })
    }
    setSending(false)
  }

  const tradeShowOptions =
    tradeshows
      ?.filter((ts) => ts.id !== targetTradeShowId)
      .map((ts) => ({
        value: ts.id,
        label: ts.name,
      })) || []

  const allSelected = eligibleRecipients && eligibleRecipients.length > 0 && selectedEmails.size === eligibleRecipients.length
  const someSelected = selectedEmails.size > 0 && selectedEmails.size < (eligibleRecipients?.length || 0)

  return (
    <Modal size="calc(100vw - 100px)" opened={opened} onClose={onClose} title={`Pre-Register Emails for ${targetTradeShowName}`}>
      <Box pos="relative" mih={400}>
        <LoadingOverlay visible={loadingRecipients} />
        <Stack spacing="md">
          {/* Source Trade Show Selection */}
          <Select label="Select Source Trade Show" placeholder="Choose a past event" data={tradeShowOptions} value={sourceTradeShowId} onChange={handleSourceChange} searchable />

          {/* Test Email Section */}
          {sourceTradeShowId && (
            <Box>
              <Text fw={500} size="sm" mb="xs">
                Test Email
              </Text>
              <Group>
                <TextInput placeholder="email@example.com" value={testEmail} onChange={(e) => setTestEmail(e.currentTarget.value)} style={{ flex: 1 }} />
                <Button onClick={handleSendTest} loading={sendingTest} variant="outline" size="sm">
                  Send Test
                </Button>
              </Group>
            </Box>
          )}

          {/* Eligible Recipients */}
          {eligibleRecipients && eligibleRecipients.length > 0 && (
            <Box>
              <Group position="apart" mb="xs">
                <Text fw={500} size="sm">
                  Eligible Recipients ({eligibleRecipients.length})
                </Text>
                <Checkbox
                  label={allSelected ? 'Deselect All' : 'Select All'}
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(e) => handleSelectAll(e.currentTarget.checked)}
                />
              </Group>

              <ScrollArea style={{ height: 400 }}>
                <Table striped highlightOnHover>
                  <thead>
                    <tr>
                      <th style={{ width: 50 }}>Select</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Company</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligibleRecipients.map((recipient) => (
                      <tr key={recipient.email}>
                        <td>
                          <Checkbox checked={selectedEmails.has(recipient.email)} onChange={(e) => handleSelectRecipient(recipient.email, e.currentTarget.checked)} />
                        </td>
                        <td>
                          {recipient.firstName} {recipient.lastName}
                        </td>
                        <td>{recipient.email}</td>
                        <td>{recipient.companyName}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollArea>
            </Box>
          )}

          {eligibleRecipients && eligibleRecipients.length === 0 && (
            <Alert icon={<IconAlertCircle size={16} />} title="No Eligible Recipients" color="blue">
              No eligible recipients found from the selected source trade show. This means everyone has either already registered or has been sent an invitation.
            </Alert>
          )}

          {/* Send Results */}
          {sendResults && (
            <Box>
              <Divider my="md" />
              <Text fw={500} size="sm" mb="xs">
                Send Results
              </Text>
              <Group spacing="xl">
                <Group spacing="xs">
                  <IconCheck size={20} color="green" />
                  <Text color="green">Successful: {sendResults.successCount}</Text>
                </Group>
                <Group spacing="xs">
                  <IconX size={20} color="red" />
                  <Text color="red">Failed: {sendResults.failureCount}</Text>
                </Group>
              </Group>

              {sendResults.failures && sendResults.failures.length > 0 && (
                <Box mt="md">
                  <Text size="sm" fw={500} mb="xs">
                    Failed Emails:
                  </Text>
                  <ScrollArea style={{ maxHeight: 150 }}>
                    {sendResults.failures.map((failure: SendEmailResult) => (
                      <Text key={failure.email} size="xs" color="red">
                        {failure.email}: {failure.error}
                      </Text>
                    ))}
                  </ScrollArea>
                </Box>
              )}
            </Box>
          )}

          {/* Action Buttons */}
          <Group position="right" mt="md">
            <Button variant="outline" color="red" onClick={onClose}>
              Close
            </Button>

            {eligibleRecipients && eligibleRecipients.length > 0 && (
              <Button onClick={handleSendEmails} loading={sending} disabled={selectedEmails.size === 0 || sending} color="green">
                Send to {selectedEmails.size} Recipients
              </Button>
            )}
          </Group>
        </Stack>
      </Box>
    </Modal>
  )
}
